import { useState } from 'react';
import { useSelector } from 'react-redux';

import { Pagination, Popover, Tooltip } from 'antd';
import moment from 'moment';

import {
  CalendarClockIcon,
  ClickedIcon,
  DeliveredEmailIcon,
  DobIcon,
  EmailOpenIcon,
  NotificationMutedIcon,
  SmsIcon,
  VisibilityoffNotificationIcon,
  WarningIcon,
} from 'src/icons';
import { NameIcon } from 'src/icons/Name.icon';
import { ModalComponent, ProfileLogo } from '../baseComponents';
import { Table } from '../groupComponents';
import './notificationHistory.scss';

const paginate = (array, pageSize, pageNumber) => {
  const startIndex = (pageNumber - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  return array.slice(startIndex, endIndex);
};

const singularDuration = (duration) => {
  switch (duration) {
    case 'Hours':
      return 'Hour';
    case 'Days':
      return 'Day';
    case 'Weeks':
      return 'Week';
    default:
      return duration;
  }
};

const NotificationsPreferencesView = ({ preferences }) => {
  const preferencesColumn = [
    {
      dataIndex: 'preference',
      render: (text) => {
        return text == 1 ? 'Default' : `Reminder ${text}`;
      },
    },
    {
      dataIndex: 'number_of',
    },
    {
      dataIndex: 'duration',
      render: (text, record) => {
        if (record.number_of == 1) {
          return `${singularDuration(text)}`;
        }
        return `${text}`;
      },
    },
  ];
  return (
    <Table
      className={'notifications-preferences-table'}
      data={preferences}
      columns={preferencesColumn}
      locale={{ emptyText: 'No Data' }}
      loading={false}
      showHeader={false}
    />
  );
};
const NotificationHistoryView = ({
  record,
  notificationHistoryDetails,
  filteredData,
}) => {
  const isMobile = window.innerWidth < 1260;
  let notificationSentFilteredData = [];
  let notificationScheduledFilteredData = [];

  const isOptOut = record?.isOptOut;

  const capitalizeFirstLetter = (str) => {
    return str?.charAt(0).toUpperCase() + str?.slice(1);
  };

  const [notificationSentPageState, setNotificationSentPageState] = useState({
    current: 1,
    pageSize: 3,
  });

  const [notificationScheduledPageState, setNotificationScheduledPageState] =
    useState({
      current: 1,
      pageSize: 3,
    });

  if (notificationHistoryDetails?.sent_notifications.length > 0) {
    if (
      notificationHistoryDetails?.sent_notifications.length <=
      notificationSentPageState.pageSize
    ) {
      notificationSentFilteredData =
        notificationHistoryDetails?.sent_notifications;
    } else {
      notificationSentFilteredData = paginate(
        notificationHistoryDetails?.sent_notifications,
        notificationSentPageState.pageSize,
        notificationSentPageState.current
      );
    }
  }

  if (notificationHistoryDetails?.schedulded_notifications.length > 0) {
    if (
      notificationHistoryDetails?.schedulded_notifications.length <=
      notificationScheduledPageState.pageSize
    ) {
      notificationScheduledFilteredData =
        notificationHistoryDetails?.schedulded_notifications;
    } else {
      notificationScheduledFilteredData = paginate(
        notificationHistoryDetails?.schedulded_notifications,
        notificationScheduledPageState.pageSize,
        notificationScheduledPageState.current
      );
    }
  }

  const notificationSentTablecolumns = [
    {
      title: 'Date',
      dataIndex: 'notification_date',
      render: (date) => {
        const utcDate = moment.utc(date, 'MMMM Do, YYYY h:mm A');
        const localDate = utcDate.local();
        const localDateString = localDate.format('MMMM Do, YYYY h:mm A');
        return localDateString;
      },
    },
    {
      title: 'Sent to',
      dataIndex: 'sent_to',
      render: (text) => {
        const isEmail = text.includes('@');
        return (
          <div className="flex items-center gap-2">
            {isEmail ? (
              <DeliveredEmailIcon
                className="color-89CFF0"
                isClickable={false}
              />
            ) : (
              <SmsIcon isClickable={false} />
            )}
            <span>{text}</span>
          </div>
        );
      },
    },
    {
      title: 'Delivery status',
      dataIndex: 'delivery_status',
      render: (text) => {
        let deliveryStatusIcon;
        let statusTooltipText;
        switch (text?.toLowerCase()) {
          case 'clicked':
          case 'click':
            deliveryStatusIcon = <ClickedIcon />;
            statusTooltipText = 'Clicked';
            break;
          case 'opened':
          case 'open':
            deliveryStatusIcon = <EmailOpenIcon />;
            statusTooltipText = 'Opened';
            break;
          case 'delivery':
          case 'delivered':
            statusTooltipText = 'Delivered';
            deliveryStatusIcon = <VisibilityoffNotificationIcon />;
            break;
          case 'sent':
          case 'send':
            statusTooltipText = 'Sent';
            deliveryStatusIcon = <VisibilityoffNotificationIcon />;
            break;
          case 'failed':
          case 'complaint':
            statusTooltipText = 'Failed';
            deliveryStatusIcon = <WarningIcon />;
            break;
          case 'bounced':
            statusTooltipText = 'Bounced';
            deliveryStatusIcon = <WarningIcon />;
            break;
          case 'Bounce':
            statusTooltipText = 'Bounced';
            deliveryStatusIcon = <WarningIcon />;
            break;
          case 'rejected':
          case 'reject':
            statusTooltipText = 'Rejected';
            deliveryStatusIcon = <WarningIcon />;
            break;
          case 'exception':
            statusTooltipText = 'Exception';
            deliveryStatusIcon = <WarningIcon />;
            break;
          case 'phone number is invalid':
          case 'email is invalid':
            statusTooltipText = capitalizeFirstLetter(text);
            deliveryStatusIcon = <WarningIcon />;
            break;
          default:
            deliveryStatusIcon = capitalizeFirstLetter(text);
        }
        return (
          <div className="flex justify-center">
            <Popover placement="topLeft" content={statusTooltipText}>
              <div>{deliveryStatusIcon}</div>
            </Popover>
          </div>
        );
      },
    },
    {
      title: 'Sent by',
      dataIndex: 'sent_by',
      render: (text, record) => (
        <div className=" flex items-center gap-2">
          <div className="shrink-0">
            {text === null
              ? 'Not available'
              : capitalizeFirstLetter(
                  text === 'scheduled' ? 'automation' : text
                )}
          </div>
          {text === 'scheduled' && record?.preferences?.length > 0 && (
            <span>
              <Popover
                placement="leftTop"
                content={
                  <NotificationsPreferencesView
                    preferences={record?.preferences}
                  />
                }
              >
                <span>
                  <CalendarClockIcon className="color-8BCB53" />
                </span>
              </Popover>
            </span>
          )}
        </div>
      ),
    },
  ];

  const notificationSentMobilecolumns = [
    {
      title: ' ',
      dataIndex: 'notification_date',
      render: (text, record) => {
        const utcDate = moment.utc(
          record.notification_date,
          'MMMM Do, YYYY h:mm A'
        );
        const localDate = utcDate.local();
        const localDateString = localDate.format('MMMM Do, YYYY h:mm A');

        const isEmail = record.sent_to.includes('@');
        const deliveryStatus = getDeliveryStatus(
          record.delivery_status,
          record.delivery_method
        );
        const sentByText =
          record.sent_by === null
            ? 'Not available'
            : capitalizeFirstLetter(
                record.sent_by === 'scheduled' ? 'automation' : record.sent_by
              );

        return (
          <>
            <div className="space-y-2">
              <div>
                <label className="text-black font-md font-semibold">
                  Notification date:
                </label>{' '}
                {localDateString}
              </div>
              <div className="flex items-center gap-2">
                <label className="text-black font-md font-semibold">
                  Sent to:
                </label>{' '}
                {isEmail ? (
                  <DeliveredEmailIcon
                    className="color-89CFF0"
                    isClickable={false}
                  />
                ) : (
                  <SmsIcon isClickable={false} />
                )}
                <span>{record.sent_to}</span>
              </div>
              <div className="flex items-center gap-2">
                <label className="text-black font-md font-semibold">
                  Delivery status:
                </label>
                <Popover
                  placement="topLeft"
                  content={deliveryStatus.statusTooltipText}
                >
                  <span>{deliveryStatus.icon}</span>
                </Popover>
              </div>
              <div className="flex items-center gap-2">
                <label className="text-black font-md font-semibold">
                  Sent by:
                </label>
                <div className="shrink-0">{sentByText}</div>
                {record.sent_by === 'scheduled' &&
                  record?.preferences?.length > 0 && (
                    <span>
                      <Popover
                        placement="leftTop"
                        content={
                          <NotificationsPreferencesView
                            preferences={record?.preferences}
                          />
                        }
                      >
                        <span>
                          <CalendarClockIcon className="color-8BCB53" />
                        </span>
                      </Popover>
                    </span>
                  )}
              </div>
            </div>
          </>
        );
      },
    },
  ];

  const getDeliveryStatus = (status) => {
    switch (status.toLowerCase()) {
      case 'clicked':
      case 'click':
        return { icon: <ClickedIcon />, statusTooltipText: 'Clicked' };
      case 'opened':
      case 'open':
        return { icon: <EmailOpenIcon />, statusTooltipText: 'Opened' };
      case 'delivered':
      case 'delivery':
        return {
          icon: <VisibilityoffNotificationIcon />,
          statusTooltipText: 'Delivered',
        };
      case 'sent':
      case 'send':
        return {
          icon: <VisibilityoffNotificationIcon />,
          statusTooltipText: 'Sent',
        };
      case 'failed':
      case 'complaint':
        return { icon: <WarningIcon />, statusTooltipText: 'Failed' };
      case 'bounced':
        return { icon: <WarningIcon />, statusTooltipText: 'Bounced' };
      case 'bounce':
        return { icon: <WarningIcon />, statusTooltipText: 'Bounced' };
      case 'rejected':
      case 'reject':
        return { icon: <WarningIcon />, statusTooltipText: 'Rejected' };
      case 'exception':
        return { icon: <WarningIcon />, statusTooltipText: 'Exception' };
      default:
        return capitalizeFirstLetter(status);
    }
  };

  const notificationScheduledTablecolumns = [
    {
      title: 'Notification date',
      dataIndex: 'notification_data',
      render: (date) => {
        const utcDate = moment.utc(date, 'MMMM Do, YYYY h:mm A');
        const localDate = utcDate.local();
        const localDateString = localDate.format('MMMM Do, YYYY h:mm A');
        return localDateString;
      },
    },
    {
      title: 'Send email to',
      dataIndex: 'to_send_email',
      render: (text, record) => (
        <div className=" flex items-center gap-2 ">
          {record?.muted || isOptOut ? (
            <Tooltip title="Notifications are muted" placement="bottomLeft">
              <NotificationMutedIcon color="#FF0000" />
            </Tooltip>
          ) : (
            <DeliveredEmailIcon className="color-89CFF0" isClickable={false} />
          )}
          <span>{text}</span>
        </div>
      ),
    },
    {
      title: 'Send sms to',
      dataIndex: 'to_send_sms',
      render: (text, record) => (
        <div className=" flex items-center gap-2 ">
          {record?.muted || isOptOut ? (
            <Tooltip title="Notifications are muted" placement="bottomLeft">
              <NotificationMutedIcon color="#FF0000" />
            </Tooltip>
          ) : (
            <SmsIcon isClickable={false} />
          )}
          <span>{text}</span>
        </div>
      ),
    },
    {
      title: 'Schedule',
      dataIndex: 'schedule',
    },
  ];

  const notificationScheduledMobilecolumns = [
    {
      title: ' ',
      dataIndex: 'notification_data',

      render: (text, record) => {
        const utcDate = moment.utc(
          record.notification_date,
          'MMMM Do, YYYY h:mm A'
        );
        const localDate = utcDate.local();
        const localDateString = localDate.format('MMMM Do, YYYY h:mm A');

        return (
          <>
            <div className="space-y-2">
              <div>
                <label className="text-black font-md font-semibold">
                  Notification date:
                </label>{' '}
                {localDateString}
              </div>
              <div>
                <label className="flex items-center ">
                  {' '}
                  <span className="text-black font-md font-semibold">
                    Sent email to:
                  </span>{' '}
                  <DeliveredEmailIcon
                    className="color-89CFF0 mr-2 ml-2"
                    isClickable={false}
                  />{' '}
                  <span>{record.to_send_email}</span>
                </label>
              </div>
              <div>
                <label className="flex items-center">
                  <span className="text-black font-md font-semibold">
                    Sent sms to:
                  </span>{' '}
                  <SmsIcon className="mr-2 ml-2" isClickable={false} />{' '}
                  <span>{record.to_send_sms}</span>
                </label>
              </div>
              <div>
                <label className="text-black font-md font-semibold">
                  Schedule:
                </label>{' '}
                {record.schedule}
              </div>
            </div>
          </>
        );
      },
    },
  ];
  return (
    <div className={`${isMobile ? '' : 'p-5'} notification-table-wrapper`}>
      <div className="bg-white rounded-xl shadow-md px-2 py-4 mb-2">
        <div
          className={
            isMobile
              ? ''
              : 'flex lg:justify-between items-center p-1 lg:p-1 mt-1 flex-wrap gap-2 xs:mt-0 xs:gap-3'
          }
        >
          <div
            className={
              isMobile
                ? 'flex gap-1 pl-3 items-center'
                : 'flex gap-2 items-center text-[#7F7F7F] '
            }
          >
            <div className="circle-background lg:ml-1">
              <NameIcon
                className="patientname"
                name={record?.patient_Name ? record?.patient_Name : 'N/A'}
              />
            </div>
            {record?.patient_Name}
          </div>

          <div
            className={
              isMobile
                ? 'flex pt-2 gap-4 pl-6 items-center'
                : 'flex gap-2 items-center text-[#7F7F7F] '
            }
          >
            <DobIcon />
            {record?.appointmentDateTime?.date || ''}
          </div>

          <div
            className={
              isMobile
                ? 'flex pt-2 gap-2 pl-5 items-center'
                : 'flex items-center lg: mr-2 text-[#7F7F7F]'
            }
          >
            <ProfileLogo
              filteredData={filteredData}
              name={`${record.practitioner_firstName} ${record.practitioner_lastName}`}
              className={'profile-popover-logo mr-1 sm:mr-2 lg:mr-3'}
              practitioner_name={record?.practitioner_Name}
            />
            {record?.practitioner_Name}
          </div>
        </div>
      </div>
      <div>
        <div className="font-bold py-2">Notifications sent</div>
        <div className="ant-table rounded-xl shadow-lg my-3 ">
          <Table
            className={'notificationHistory-page-table'}
            data={notificationSentFilteredData}
            columns={
              isMobile
                ? notificationSentMobilecolumns
                : notificationSentTablecolumns
            }
            loading={
              notificationHistoryDetails?.sent_notifications === undefined
                ? true
                : false
            }
            locale={{ emptyText: 'No Data' }}
          />
        </div>
        <div className="flex items-center justify-end ">
          {notificationHistoryDetails?.sent_notifications.length >
            notificationSentPageState.pageSize && (
            <Pagination
              current={notificationSentPageState.current}
              pageSize={notificationSentPageState.pageSize}
              total={notificationHistoryDetails?.sent_notifications.length}
              onChange={(page, pageSize) => {
                setNotificationSentPageState({
                  current: page,
                  pageSize,
                });
              }}
              showSizeChanger={false}
              pageSizeOptions={['10', '30', '50', '100']}
              size="small"
              locale={{ items_per_page: '' }}
            />
          )}
        </div>
        <div className="flex gap-2 items-center font-bold py-2">
          Notifications scheduled
          {notificationHistoryDetails?.practitioner_preference?.length > 0 && (
            <Popover
              content={
                <NotificationsPreferencesView
                  preferences={
                    notificationHistoryDetails?.practitioner_preference
                  }
                />
              }
            >
              <span>
                <CalendarClockIcon className="color-8BCB53" />
              </span>
            </Popover>
          )}
        </div>
        <div className="ant-table rounded-xl shadow-lg my-3">
          <Table
            className={'notificationHistory-page-table'}
            data={notificationScheduledFilteredData}
            columns={
              isMobile
                ? notificationScheduledMobilecolumns
                : notificationScheduledTablecolumns
            }
            loading={
              notificationHistoryDetails?.schedulded_notifications === undefined
                ? true
                : false
            }
            locale={{ emptyText: 'No Data' }}
          />
        </div>
        <div className="flex items-center justify-end ">
          {notificationHistoryDetails?.schedulded_notifications.length >
            notificationScheduledPageState.pageSize && (
            <Pagination
              rootClassName="schedule"
              current={notificationScheduledPageState.current}
              pageSize={notificationScheduledPageState.pageSize}
              total={
                notificationHistoryDetails?.schedulded_notifications.length
              }
              onChange={(page, pageSize) => {
                setNotificationScheduledPageState({
                  current: page,
                  pageSize,
                });
              }}
              showSizeChanger={false}
              pageSizeOptions={['10', '30', '50', '100']}
              size="small"
              locale={{ items_per_page: '' }}
            />
          )}
        </div>
      </div>
    </div>
  );
};
const NotificationHistory = ({ isOpen, setIsOpen, record, filteredData }) => {
  const { notificationHistoryDetails } = useSelector(
    (state) => state?.appointmentState
  );
  const sections = [
    {
      heading: 'Notification history',
      fields: [],
    },
  ];

  return (
    <ModalComponent
      open={isOpen}
      sections={sections}
      handleCancel={() => setIsOpen(false)}
      submitButtonText=""
      cancelButtonText=""
      centered={true}
      additionalBody={
        <NotificationHistoryView
          record={record}
          notificationHistoryDetails={notificationHistoryDetails}
          filteredData={filteredData}
        />
      }
      className={`max-h-screen notificationHistory-page-table`}
    />
  );
};

export default NotificationHistory;
